import axios, { isAxiosError } from 'axios';
import type { OrderbookResponse } from '../types/orderbook';
import type { ArbitrageOpportunity } from '../types/opportunity';

// ALWAYS use the full backend URL directly - NO PROXY
// Priority: VITE_API_URL environment variable, fallback to dev.arbitre.app
const baseURL = import.meta.env.VITE_API_URL || 'https://dev.arbitre.app/api';

console.log('[OrderbookService] Configuration (direct connection):', {
  envApiUrl: import.meta.env.VITE_API_URL,
  baseURL,
  usingFallback: !import.meta.env.VITE_API_URL
});

const orderbookClient = axios.create({
  baseURL,
  timeout: 15000,
});

type ApiErrorBody = {
  error?: string;
};

export async function fetchOrderbook(exchange: string, symbol: string): Promise<OrderbookResponse> {
  try {
    const response = await orderbookClient.get<OrderbookResponse>(`/orderbook/${exchange}/${symbol}`);
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const data = error.response?.data as ApiErrorBody | undefined;
      const message = data?.error ?? error.message ?? 'Failed to fetch orderbook';
      throw new Error(message);
    }
    throw error;
  }
}

export interface OrderbookPairResponse {
  left?: OrderbookResponse;
  right?: OrderbookResponse;
  opportunity?: ArbitrageOpportunity;
}

export async function fetchOrderbookPair(
  exchange1: string,
  symbol1: string,
  exchange2: string,
  symbol2: string,
): Promise<OrderbookPairResponse> {
  try {
    const response = await orderbookClient.get<OrderbookPairResponse>(
      `/orderbooks/${exchange1}/${symbol1}/${exchange2}/${symbol2}`,
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const data = error.response?.data as ApiErrorBody | undefined;
      const message = data?.error ?? error.message ?? 'Failed to fetch orderbooks';
      throw new Error(message);
    }
    throw error;
  }
}

export interface FuturesBookTickerResponse {
  exchange: string;
  symbol: string;
  base: string;
  quote: string;
  bid_price: number;
  bid_qty: number;
  ask_price: number;
  ask_qty: number;
  spread: number;
  spread_percentage: number;
  mid_price: number;
  update_id: number;
  event_time: number;
  transaction_time: number;
  orderbook?: OrderbookResponse;
}

export interface FuturesOrderbookData {
  orderbook: OrderbookResponse;
  bookTicker: FuturesBookTickerResponse;
}

export async function fetchFuturesOrderbook(exchange: string, symbol: string): Promise<OrderbookResponse> {
  try {
    const response = await orderbookClient.get<OrderbookResponse>(`/f/orderbook/${exchange}/${symbol}`);
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const data = error.response?.data as ApiErrorBody | undefined;
      const message = data?.error ?? error.message ?? 'Failed to fetch futures orderbook';
      throw new Error(message);
    }
    throw error;
  }
}

export async function fetchFuturesOrderbookWithTicker(exchange: string, symbol: string): Promise<FuturesOrderbookData> {
  try {
    const response = await orderbookClient.get<FuturesBookTickerResponse>(`/f/bookticker/${exchange}/${symbol}`);

    // If orderbook is included in the response, return both
    if (response.data.orderbook) {
      return {
        orderbook: response.data.orderbook,
        bookTicker: response.data,
      };
    }

    // Otherwise, throw error indicating orderbook not available
    throw new Error('Orderbook data not available in book ticker response');
  } catch (error) {
    if (isAxiosError(error)) {
      const data = error.response?.data as ApiErrorBody | undefined;
      const message = data?.error ?? error.message ?? 'Failed to fetch futures orderbook with ticker';
      throw new Error(message);
    }
    throw error;
  }
}
